import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Storefront, MapPin, Phone, Globe, Users, Trophy, Calendar, GameController, Check, Clock, Medal, Tag, Star, DiceFive, Lightning } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import EventCard from '../components/EventCard';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopDetailPage() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [shop, setShop] = useState(null);
  const [events, setEvents] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [publicData, setPublicData] = useState(null);
  const [gameLibrary, setGameLibrary] = useState([]);
  const [libFilter, setLibFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');

  const isMember = user?.home_shop_id === shopId;

  useEffect(() => {
    fetchData();
  }, [shopId]);

  const fetchData = async () => {
    try {
      const [shopRes, eventsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/shops/${shopId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shops/${shopId}/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/shops/${shopId}/leaderboard`, { withCredentials: true })
      ]);
      setShop(shopRes.data);
      setEvents(eventsRes.data);
      setLeaderboard(leaderboardRes.data);
      // Also fetch public page data for enhanced info
      try {
        const publicRes = await axios.get(`${API_URL}/api/shop-portal/public/${shopId}`);
        setPublicData(publicRes.data);
      } catch {}
      // Fetch game library
      try {
        const libRes = await axios.get(`${API_URL}/api/shops/${shopId}/library`);
        setGameLibrary(libRes.data);
      } catch {}
    } catch (error) {
      console.error('Error fetching shop:', error);
      toast.error('Shop not found');
      navigate('/shops');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      await axios.post(`${API_URL}/api/shops/${shopId}/join`, {}, { withCredentials: true });
      updateUser({ home_shop_id: shopId });
      toast.success('Joined shop!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleRsvp = async (eventId, status) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/shop-portal/rsvp/${eventId}`, { status }, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleJoinOpenPlay = async (postId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/shop-portal/open-play/${postId}/join`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!shop) return null;

  const shopEvents = publicData?.upcoming_events || [];
  const openPlayPosts = publicData?.open_play_posts || [];
  const shopTournaments = publicData?.tournaments || [];
  const shopBadges = publicData?.badges || [];

  return (
    <div className="app-container" data-testid="shop-detail-page">
      {/* Header */}
      <div className="relative h-40">
        {shop.image ? (
          <img src={shop.image} alt={shop.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#9D4CDD]/20 to-[#00F0FF]/20 flex items-center justify-center">
            <Storefront size={64} weight="duotone" className="text-[#00F0FF]" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#06060A] to-transparent"></div>
        <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-10 h-10 rounded-full glass flex items-center justify-center" data-testid="back-button">
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
      </div>

      {/* Shop Info */}
      <div className="px-6 -mt-8 relative z-10 pb-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{shop.name}</h1>
            {shop.verification_badge && (
              <span className="text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${shop.verification_badge_color || '#3B82F6'}15`, color: shop.verification_badge_color || '#3B82F6' }}
                data-testid="shop-verification-badge">
                {shop.verification_badge}
              </span>
            )}
            {!shop.verification_badge && (shop.verified || publicData?.verified) && <Check size={20} className="text-[#00F0FF]" />}
          </div>
          <p className="text-[#A1A1AA] flex items-center gap-1 mt-2"><MapPin size={16} weight="duotone" className="text-[#9D4CDD]" /> {shop.location}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-3">
            {(publicData?.beginner_friendly || shop.beginner_friendly) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981]">Beginner Friendly</span>}
            {(publicData?.family_friendly) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3B82F6]/10 text-[#3B82F6]">Family Friendly</span>}
            {(publicData?.open_for_free_play || shop.open_play) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#9D4CDD]/10 text-[#9D4CDD]">Open Play</span>}
          </div>

          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-1 text-[#39FF14] text-sm"><Users size={16} weight="duotone" /> {publicData?.member_count || shop.member_count || 0} members</div>
          </div>

          <p className="text-[#A1A1AA] mt-4 leading-relaxed">{shop.description}</p>

          {/* Contact */}
          <div className="mt-4 space-y-2">
            {(shop.address || publicData?.address_line_1) && <p className="text-[#71717A] text-sm flex items-center gap-2"><MapPin size={14} /> {shop.address || publicData?.address_line_1}</p>}
            {(shop.phone || publicData?.phone) && <p className="text-[#71717A] text-sm flex items-center gap-2"><Phone size={14} /> {shop.phone || publicData?.phone}</p>}
            {(shop.website || publicData?.website_url) && <a href={shop.website || publicData?.website_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] text-sm flex items-center gap-2"><Globe size={14} /> {shop.website || publicData?.website_url}</a>}
          </div>

          {/* Champion */}
          {publicData?.champion_name && (
            <div className="mt-4 p-3 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/20 flex items-center gap-2">
              <Trophy size={18} className="text-[#F59E0B]" />
              <span className="text-xs text-[#F59E0B]">Shop Champion:</span>
              <span className="text-white text-sm font-semibold">{publicData.champion_name}</span>
            </div>
          )}

          {/* Join Button */}
          {!isMember ? (
            <button onClick={handleJoin} className="btn-primary w-full mt-6" data-testid="join-shop">Set as Home Shop</button>
          ) : (
            <div className="mt-6 p-3 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14] text-sm text-center">This is your home shop</div>
          )}

          {/* Tabs */}
          <div className="flex gap-1.5 mt-6 overflow-x-auto pb-1">
            {['events', 'game_library', 'open_play', 'tournaments', 'rankings', 'badges'].map(t => (
              <button key={t} onClick={() => setActiveTab(t)}
                className={`py-2 px-3 rounded-xl font-medium text-xs whitespace-nowrap transition-all flex items-center gap-1.5 ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}>
                {t === 'events' && <Calendar size={14} />}
                {t === 'game_library' && <GameController size={14} />}
                {t === 'open_play' && <GameController size={14} />}
                {t === 'tournaments' && <Trophy size={14} />}
                {t === 'rankings' && <Users size={14} />}
                {t === 'badges' && <Medal size={14} />}
                {t === 'game_library' ? 'Game Library' : t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'events' && (
              <div>
                {/* Portal events */}
                {shopEvents.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {shopEvents.map(e => (
                      <div key={e.id} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]">{(e.event_type || '').replace(/_/g, ' ')}</span>
                          {e.beginner_friendly && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981]">Beginner</span>}
                        </div>
                        <p className="text-white font-medium">{e.title}</p>
                        <div className="flex items-center gap-3 text-xs text-[#777] mt-1">
                          {e.game_name && <span>{e.game_name}</span>}
                          <span><Clock size={12} className="inline" /> {new Date(e.scheduled_start).toLocaleDateString()}</span>
                          <span className="text-[#00F0FF]">{e.open_seats} seats open</span>
                        </div>
                        <button onClick={() => handleRsvp(e.id, 'going')} className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20" data-testid={`rsvp-event-${e.id}`}>RSVP Going</button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Existing events */}
                {events.length > 0 ? (
                  <div className="space-y-3">
                    {events.map((event, index) => (<EventCard key={event.id} event={event} index={index} />))}
                  </div>
                ) : shopEvents.length === 0 ? (
                  <div className="text-center py-8 text-[#71717A]"><Calendar size={48} weight="duotone" className="mx-auto mb-4 opacity-50" /><p>No upcoming events</p></div>
                ) : null}
              </div>
            )}

            {activeTab === 'game_library' && (
              <div data-testid="shop-game-library-tab">
                {gameLibrary.length > 0 ? (
                  <>
                    {/* Filter pills */}
                    <div className="flex gap-1.5 mb-3 flex-wrap">
                      {[null, 'sold_here', 'demo_copy', 'available_for_free_play', 'library_copy', 'event_supported', 'tournament_supported', 'currently_featured'].map(f => {
                        const labels = { null: 'All', sold_here: 'For Sale', demo_copy: 'Demo', available_for_free_play: 'Free Play', library_copy: 'Library', event_supported: 'Events', tournament_supported: 'Tournaments', currently_featured: 'Featured' };
                        const colors = { null: '#A1A1AA', sold_here: '#10B981', demo_copy: '#F59E0B', available_for_free_play: '#06B6D4', library_copy: '#3B82F6', event_supported: '#9D4CDD', tournament_supported: '#EF4444', currently_featured: '#EC4899' };
                        return (
                          <button key={String(f)} onClick={() => setLibFilter(f)}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${libFilter === f ? 'text-black' : 'text-[#888]'}`}
                            style={libFilter === f ? { background: colors[f] } : { background: 'rgba(255,255,255,0.04)' }}
                          >{labels[f]}</button>
                        );
                      })}
                    </div>
                    <div className="space-y-2">
                      {gameLibrary
                        .filter(g => !libFilter || g.statuses.includes(libFilter))
                        .map(g => {
                          const statusLabels = { sold_here: 'For Sale', demo_copy: 'Demo', library_copy: 'Library', available_for_free_play: 'Free Play', event_supported: 'Events', tournament_supported: 'Tournaments', currently_featured: 'Featured', temporarily_unavailable: 'Unavailable' };
                          const statusColors = { sold_here: '#10B981', demo_copy: '#F59E0B', available_for_free_play: '#06B6D4', library_copy: '#3B82F6', event_supported: '#9D4CDD', tournament_supported: '#EF4444', currently_featured: '#EC4899', temporarily_unavailable: '#6B7280' };
                          return (
                            <div key={g.game_id} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`lib-game-${g.game_slug}`}>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center shrink-0">
                                  <GameController size={20} weight="duotone" className="text-[#10B981]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white font-medium text-sm">{g.game_name}</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {g.statuses.map(s => (
                                      <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${statusColors[s] || '#666'}20`, color: statusColors[s] || '#666' }}>
                                        {statusLabels[s] || s}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="text-right">
                                  {g.game_complexity && <span className="text-[9px] text-[#555] block">{g.game_complexity}</span>}
                                  {g.game_min_players && <span className="text-[9px] text-[#555]">{g.game_min_players}-{g.game_max_players}p</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-[#71717A]">
                    <GameController size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
                    <p>No games in this shop's library yet</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'open_play' && (
              <div className="space-y-3">
                {openPlayPosts.length > 0 ? openPlayPosts.map(p => (
                  <div key={p.id} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                    <p className="text-white font-medium">{p.title}</p>
                    <p className="text-xs text-[#A1A1AA] mt-1">{p.description}</p>
                    <div className="flex items-center gap-3 text-xs text-[#777] mt-2">
                      {p.game_name && <span>{p.game_name}</span>}
                      <span><Clock size={12} className="inline" /> {p.scheduled_start ? new Date(p.scheduled_start).toLocaleDateString() : ''}</span>
                      <span className="text-[#9D4CDD]">{Math.max(0, (p.seats_available || 0) - (p.seats_taken || 0))} seats open</span>
                    </div>
                    <button onClick={() => handleJoinOpenPlay(p.id)} className="mt-2 text-xs px-3 py-1.5 rounded-lg bg-[#9D4CDD]/10 text-[#9D4CDD] hover:bg-[#9D4CDD]/20" data-testid={`join-openplay-${p.id}`}>Join Table</button>
                  </div>
                )) : <div className="text-center py-8 text-[#71717A]"><GameController size={48} weight="duotone" className="mx-auto mb-4 opacity-50" /><p>No open play posts</p></div>}
              </div>
            )}

            {activeTab === 'tournaments' && (
              <div className="space-y-3">
                {shopTournaments.length > 0 ? shopTournaments.map(t => (
                  <div key={t.id} className="p-4 rounded-xl bg-[#0F0F16] border border-white/5">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-medium">{t.name}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]">{t.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#777] mt-1">
                      <span>{t.game_type}</span>
                      <span><Clock size={12} className="inline" /> {t.date}</span>
                      <span><Users size={12} className="inline" /> {t.current_players}/{t.max_players}</span>
                    </div>
                  </div>
                )) : <div className="text-center py-8 text-[#71717A]"><Trophy size={48} weight="duotone" className="mx-auto mb-4 opacity-50" /><p>No tournaments</p></div>}
              </div>
            )}

            {activeTab === 'rankings' && (
              leaderboard.length > 0 ? (
                <div className="space-y-2">
                  {leaderboard.map((entry) => (
                    <div key={entry.user_id} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
                      <span className="w-6 text-[#71717A] font-bold">#{entry.rank}</span>
                      <div className="flex-1"><p className="text-white font-medium">{entry.name}</p><p className="text-[#71717A] text-xs">@{entry.username}</p></div>
                      <div className="text-right"><p className="text-white font-bold">Lv.{entry.level}</p><p className="text-[#71717A] text-xs">{entry.xp} XP</p></div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-center py-8 text-[#71717A]"><Trophy size={48} weight="duotone" className="mx-auto mb-4 opacity-50" /><p>No rankings yet</p></div>
            )}

            {activeTab === 'badges' && (
              <div className="grid grid-cols-2 gap-3">
                {shopBadges.length > 0 ? shopBadges.map((b, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 text-center">
                    <Medal size={28} weight="duotone" className="text-[#EC4899] mx-auto mb-1" />
                    <p className="text-white text-sm font-medium">{b.name}</p>
                    <p className="text-[10px] text-[#777] mt-0.5">{b.description}</p>
                  </div>
                )) : <div className="col-span-2 text-center py-8 text-[#71717A]"><Medal size={48} weight="duotone" className="mx-auto mb-4 opacity-50" /><p>No badges yet</p></div>}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
